import React from 'react';
import {
    Box,
    Card,
    CardContent,
    Container,
    Divider,
    Grid,
    makeStyles,
} from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import Page from 'src/components/Page';

const INFORMATION_CARDS = [
    { id: 'lead', rows: 15 },
    { id: 'vehicle', rows: 8 },
    { id: 'follow-up', rows: 8 },
    { id: 'appointment', rows: 6 },
];
const TASK_ITEMS = ['task-1', 'task-2', 'task-3'];
const ACTION_CARDS = ['contacted', 'documentation', 'whatsapp', 'appointment'];

const useStyles = makeStyles((theme) => ({
    root: {
        backgroundColor: theme.palette.background.dark,
        minHeight: '100%',
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(1),
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing(2),
        [theme.breakpoints.down('xs')]: {
            alignItems: 'flex-start',
            flexDirection: 'column',
            gap: theme.spacing(2),
        },
    },
    headerActions: {
        display: 'flex',
        gap: theme.spacing(1),
    },
    tabs: {
        display: 'flex',
        gap: theme.spacing(4),
        padding: theme.spacing(1, 2),
    },
    columns: {
        marginTop: theme.spacing(2),
    },
    column: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
    cardHeader: {
        padding: theme.spacing(2),
    },
    informationRow: {
        display: 'flex',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(1.5),
    },
    input: {
        borderRadius: theme.shape.borderRadius,
    },
    taskHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
    },
    taskTitle: {
        flexGrow: 1,
    },
    taskContent: {
        paddingLeft: theme.spacing(8),
    },
    taskFooter: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: theme.spacing(1),
        marginTop: theme.spacing(2),
    },
    actionButton: {
        borderRadius: theme.shape.borderRadius,
    },
}));

const InformationCardSkeleton = ({ rows }: { rows: number }) => {
    const classes = useStyles();

    return (
        <Card>
            <Box className={classes.cardHeader}>
                <Skeleton height={30} width="55%" />
            </Box>
            <Divider />
            <CardContent>
                {Array.from({ length: rows }).map((_, index) => (
                    <Box
                        className={classes.informationRow}
                        key={`information-row-${rows}-${index + 1}`}
                    >
                        <Skeleton height={22} width="38%" />
                        <Skeleton height={22} width="48%" />
                    </Box>
                ))}
            </CardContent>
        </Card>
    );
};

const TaskComposerSkeleton = () => {
    const classes = useStyles();

    return (
        <Card>
            <Box className={classes.cardHeader}>
                <Skeleton height={32} width={110} />
            </Box>
            <Divider />
            <CardContent>
                <Skeleton
                    className={classes.input}
                    height={56}
                    variant="rect"
                    width="100%"
                />
            </CardContent>
        </Card>
    );
};

const TaskItemSkeleton = () => {
    const classes = useStyles();

    return (
        <Card>
            <CardContent>
                <Box className={classes.taskHeader}>
                    <Skeleton height={42} variant="circle" width={42} />
                    <Box className={classes.taskTitle}>
                        <Skeleton height={25} width="38%" />
                        <Skeleton height={18} width="28%" />
                    </Box>
                    <Skeleton height={25} variant="rect" width={90} />
                </Box>
                <Box className={classes.taskContent}>
                    <Skeleton height={20} width="86%" />
                    <Skeleton height={20} width="62%" />
                    <Box className={classes.taskFooter}>
                        <Skeleton height={36} variant="rect" width={90} />
                        <Skeleton height={36} variant="rect" width={120} />
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

const StatusCardSkeleton = () => {
    const classes = useStyles();

    return (
        <Card>
            <Box className={classes.cardHeader}>
                <Skeleton height={30} width="42%" />
            </Box>
            <Divider />
            <CardContent>
                <Skeleton height={56} variant="rect" width="100%" />
                <Box mt={2}>
                    <Skeleton height={56} variant="rect" width="100%" />
                </Box>
                <Box mt={2}>
                    <Skeleton height={52} variant="rect" width="100%" />
                </Box>
            </CardContent>
        </Card>
    );
};

const ActionCardSkeleton = () => {
    const classes = useStyles();

    return (
        <Card>
            <CardContent>
                <Skeleton height={25} width="52%" />
                <Box mt={1.5}>
                    <Skeleton
                        className={classes.actionButton}
                        height={46}
                        variant="rect"
                        width="100%"
                    />
                </Box>
            </CardContent>
        </Card>
    );
};

export const LeadDetailsSkeleton = () => {
    const classes = useStyles();

    return (
        <Page className={classes.root} title="">
            <Container maxWidth={false}>
                <Box className={classes.header}>
                    <Box>
                        <Skeleton height={46} width={280} />
                        <Skeleton height={22} width={210} />
                    </Box>
                    <Box className={classes.headerActions}>
                        <Skeleton height={42} variant="rect" width={105} />
                        <Skeleton height={42} variant="rect" width={105} />
                        <Skeleton height={42} variant="rect" width={105} />
                    </Box>
                </Box>
                <Box className={classes.tabs}>
                    <Skeleton height={36} width={72} />
                    <Skeleton height={36} width={90} />
                    <Skeleton height={36} width={100} />
                    <Skeleton height={36} width={78} />
                </Box>
                <Divider />
                <Grid className={classes.columns} container spacing={2}>
                    <Grid item lg={3} xs={12}>
                        <Box className={classes.column}>
                            {INFORMATION_CARDS.map(({ id, rows }) => (
                                <InformationCardSkeleton key={id} rows={rows} />
                            ))}
                        </Box>
                    </Grid>
                    <Grid item lg={6} md={6} xl={6} xs={12}>
                        <Box className={classes.column}>
                            <TaskComposerSkeleton />
                            {TASK_ITEMS.map((taskId) => (
                                <TaskItemSkeleton key={taskId} />
                            ))}
                        </Box>
                    </Grid>
                    <Grid item lg={3} xs={12}>
                        <Box className={classes.column}>
                            <StatusCardSkeleton />
                            {ACTION_CARDS.map((actionId) => (
                                <ActionCardSkeleton key={actionId} />
                            ))}
                        </Box>
                    </Grid>
                </Grid>
            </Container>
        </Page>
    );
};
